import { z } from 'zod'

// Client-side pre-checks mirroring the Go handlers (apps/api/server/handlers.go,
// apps/api/auth/auth.go), so obviously-invalid input never makes a round trip.
// The API remains authoritative — these only improve the error a person sees.

/** Mirrors audit.ParseTarget / seo.ParseTarget: needs an http(s) host with a dot. */
function isValidDomain(value: string): boolean {
  const withScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(value) ? value : `https://${value}`
  try {
    const url = new URL(withScheme)
    return (url.protocol === 'http:' || url.protocol === 'https:') && url.hostname.includes('.')
  } catch {
    return false
  }
}

export const DomainSchema = z
  .string()
  .trim()
  .min(1, 'Enter your website.')
  .refine(isValidDomain, 'Please enter a valid website (e.g. example.com).')

/** Email is optional for audit/seo — blank is fine, anything else must look like an address. */
export const OptionalEmailSchema = z
  .string()
  .refine((value) => value === '' || z.email().safeParse(value).success, {
    message: 'Please enter a valid email address.',
  })

const PASSWORD_MIN = 10
const PASSWORD_MAX = 128

const PasswordSchema = z
  .string()
  .min(PASSWORD_MIN, `Use ${PASSWORD_MIN} to ${PASSWORD_MAX} characters.`)
  .max(PASSWORD_MAX, `Use ${PASSWORD_MIN} to ${PASSWORD_MAX} characters.`)

export const LoginSchema = z.object({
  email: z.email('Enter a valid email address.'),
  password: z.string().min(1, 'Enter your password.'),
})

export const RegisterSchema = z.object({
  email: z.email('Enter a valid email address.'),
  password: PasswordSchema,
})

export const ChangePasswordSchema = z.object({
  newPassword: PasswordSchema,
})

export const CreateIntakeSchema = z.object({
  client_name: z
    .string()
    .trim()
    .min(1, 'Enter a client name between 1 and 200 characters.')
    .max(200, 'Enter a client name between 1 and 200 characters.'),
  form_type: z.string().min(1, 'Choose an intake template.'),
})

/** Returns the first validation message, or undefined when `result` succeeded. */
export function firstIssue(result: z.ZodSafeParseResult<unknown>): string | undefined {
  return result.success ? undefined : result.error.issues[0]?.message
}
